import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Spinner } from 'react-bootstrap';
import { FaCar, FaPlus, FaEdit, FaTrash, FaSearch, FaList, FaThLarge, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaCog, FaSync } from 'react-icons/fa';
import { GetFromApi, PostToApi, PutToApi, DeleteFromApi } from '../utils/api';
import socketService from '../utils/socket';
import '../styles/Pages.css';

const Vehicles = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [formData, setFormData] = useState({
    number: '',
    make: '',
    model: '',
    year: '',
    color: '',
    driver_name: '',
    driver_phone: '',
    device_id: '',
    fuel_type: 'Petrol',
    status: 'active'
  });

  // Fetch vehicles from API
  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/vehicles');
      if (response.success) {
        setVehicles(response.data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();

    // Subscribe to real-time updates
    socketService.connect();
    socketService.on('vehicle:location', (data) => {
      setVehicles(prev => prev.map(v => 
        v.id === data.vehicleId ? { ...v, speed: data.speed, lat: data.lat, lng: data.lng, heading: data.heading } : v
      ));
    });
    socketService.on('vehicle:created', (vehicle) => {
      setVehicles(prev => [vehicle, ...prev]);
    });
    socketService.on('vehicle:updated', (vehicle) => {
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? vehicle : v));
    });
    socketService.on('vehicle:deleted', ({ id }) => {
      setVehicles(prev => prev.filter(v => v.id !== id));
    });

    return () => {
      socketService.off('vehicle:location');
      socketService.off('vehicle:created');
      socketService.off('vehicle:updated');
      socketService.off('vehicle:deleted');
    };
  }, []);

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchSearch = vehicle.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       vehicle.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || vehicle.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#22C55E';
      case 'maintenance': return '#F59E0B';
      case 'offline': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'maintenance': return 'Maintenance';
      case 'offline': return 'Offline';
      default: return status;
    }
  };

  const handleOpenModal = (vehicle = null) => {
    if (vehicle) {
      setEditMode(true);
      setFormData({
        number: vehicle.number || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
        color: vehicle.color || '',
        driver_name: vehicle.driver_name || '',
        driver_phone: vehicle.driver_phone || '',
        device_id: vehicle.device_id || '',
        fuel_type: vehicle.fuel_type || 'Petrol',
        status: vehicle.status || 'active'
      });
      setSelectedVehicle(vehicle);
    } else {
      setEditMode(false);
      setFormData({
        number: '',
        make: '',
        model: '',
        year: '',
        color: '',
        driver_name: '',
        driver_phone: '',
        device_id: '',
        fuel_type: 'Petrol',
        status: 'active'
      });
      setSelectedVehicle(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVehicle(null);
    setEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMode && selectedVehicle) {
        await PutToApi(`/vehicles/${selectedVehicle.id}`, formData);
      } else {
        await PostToApi('/vehicles', formData);
      }
      await fetchVehicles();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Error saving vehicle: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await DeleteFromApi(`/vehicles/${id}`);
        await fetchVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        alert('Error deleting vehicle: ' + error.message);
      }
    }
  };

  const activeCount = vehicles.filter(v => v.status === 'active').length;
  const maintenanceCount = vehicles.filter(v => v.status === 'maintenance').length;
  const offlineCount = vehicles.filter(v => v.status === 'offline').length;

  return (
    <Container fluid className="p-3">
      {/* Header */}
      <div className="page-header">
        <h4 className="page-title">Vehicle Management</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchVehicles} disabled={loading} style={{ borderRadius: '8px' }}>
            {loading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
          <div className="btn-group">
            <Button 
              variant={viewMode === 'grid' ? 'primary' : 'light'}
              onClick={() => setViewMode('grid')}
              style={{ borderRadius: '8px 0 0 8px' }}
            >
              <FaThLarge />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'primary' : 'light'}
              onClick={() => setViewMode('list')}
              style={{ borderRadius: '0 8px 8px 0' }}
            >
              <FaList />
            </Button>
          </div>
          <Button 
            variant="primary" 
            onClick={() => handleOpenModal()}
            style={{ borderRadius: '8px' }}
          >
            <FaPlus className="me-2" /> Add Vehicle
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}>
                <FaCar />
              </div>
              <div>
                <div className="stats-value">{vehicles.length}</div>
                <div className="stats-label">Total Vehicles</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card 
            className={`stats-card border-0 shadow-sm ${filterStatus === 'active' ? 'border border-2 border-success' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setFilterStatus(filterStatus === 'active' ? 'all' : 'active')}
          >
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22C55E' }}>
                <FaCar />
              </div>
              <div>
                <div className="stats-value">{activeCount}</div>
                <div className="stats-label">Active</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card 
            className={`stats-card border-0 shadow-sm ${filterStatus === 'maintenance' ? 'border border-2 border-warning' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setFilterStatus(filterStatus === 'maintenance' ? 'all' : 'maintenance')}
          >
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }}>
                <FaCog />
              </div>
              <div>
                <div className="stats-value">{maintenanceCount}</div>
                <div className="stats-label">Maintenance</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card 
            className={`stats-card border-0 shadow-sm ${filterStatus === 'offline' ? 'border border-2 border-danger' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setFilterStatus(filterStatus === 'offline' ? 'all' : 'offline')}
          >
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}>
                <FaCar />
              </div>
              <div>
                <div className="stats-value">{offlineCount}</div>
                <div className="stats-label">Offline</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Card className="page-card mb-4">
        <Card.Body className="py-3">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <Form.Control
              type="text"
              placeholder="Search by vehicle number, driver, or make..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', borderRadius: '8px' }}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading vehicles...</p>
        </div>
      ) : viewMode === 'grid' ? (
        <Row>
          {filteredVehicles.map(vehicle => (
            <Col key={vehicle.id} lg={4} md={6} className="mb-4">
              <Card className="vehicle-management-card h-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="vehicle-icon-box">
                    <FaCar />
                  </div>
                  <Badge 
                    style={{ backgroundColor: `${getStatusColor(vehicle.status)}20`, color: getStatusColor(vehicle.status) }}
                    className="rounded-pill px-3"
                  >
                    {getStatusLabel(vehicle.status)}
                  </Badge>
                </div>
                
                <h5 className="fw-bold mb-2">{vehicle.number}</h5>
                <p className="text-muted mb-3">{vehicle.make} {vehicle.model} ({vehicle.year})</p>
                
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <FaUser className="text-primary me-2" size={14} />
                    <span className="small">{vehicle.driver_name || 'No driver assigned'}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaMapMarkerAlt className="text-danger me-2" size={14} />
                    <span className="small text-muted">{vehicle.address || 'Unknown location'}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaCalendarAlt className="text-warning me-2" size={14} />
                    <span className="small text-muted">Speed: {vehicle.speed || 0} km/h</span>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-auto">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="flex-grow-1"
                    onClick={() => handleOpenModal(vehicle)}
                  >
                    <FaEdit className="me-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDelete(vehicle.id)}
                  >
                    <FaTrash />
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="page-card">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <th>Vehicle</th>
                    <th>Driver</th>
                    <th>Device ID</th>
                    <th>Speed</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map(vehicle => (
                    <tr key={vehicle.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2 d-flex align-items-center justify-content-center"
                            style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}
                          >
                            <FaCar />
                          </div>
                          <div>
                            <div className="fw-semibold">{vehicle.number}</div>
                            <small className="text-muted">{vehicle.make} {vehicle.model}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>{vehicle.driver_name || '-'}</div>
                        <small className="text-muted">{vehicle.driver_phone || ''}</small>
                      </td>
                      <td><code>{vehicle.device_id || '-'}</code></td>
                      <td>{vehicle.speed || 0} km/h</td>
                      <td>
                        <Badge 
                          style={{ backgroundColor: `${getStatusColor(vehicle.status)}20`, color: getStatusColor(vehicle.status) }}
                          className="rounded-pill"
                        >
                          {getStatusLabel(vehicle.status)}
                        </Badge>
                      </td>
                      <td>
                        <button className="action-btn edit me-1" onClick={() => handleOpenModal(vehicle)}>
                          <FaEdit size={14} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(vehicle.id)}>
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {filteredVehicles.length === 0 && !loading && (
        <div className="empty-state py-5">
          <div className="empty-state-icon"><FaCar /></div>
          <div className="empty-state-title">No vehicles found</div>
          <div className="empty-state-text">Try adjusting your search or add a new vehicle</div>
        </div>
      )}

      {/* Add/Edit Vehicle Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered className="modal-custom" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'Edit Vehicle' : 'Add New Vehicle'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Vehicle Number</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., GJ-27-X-2413"
                    value={formData.number} onChange={(e) => setFormData({...formData, number: e.target.value})} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Device ID</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., GPS-DEV-001"
                    value={formData.device_id} onChange={(e) => setFormData({...formData, device_id: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Make</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., Tata"
                    value={formData.make} onChange={(e) => setFormData({...formData, make: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Model</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., Ace"
                    value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Year</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., 2022"
                    value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Color</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="e.g., White"
                    value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Fuel Type</Form.Label>
                  <Form.Select className="form-control-custom" value={formData.fuel_type}
                    onChange={(e) => setFormData({...formData, fuel_type: e.target.value})}>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="CNG">CNG</option>
                    <option value="Electric">Electric</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <hr className="my-4" />
            <h6 className="mb-3">Driver Information</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Driver Name</Form.Label>
                  <Form.Control type="text" className="form-control-custom" placeholder="Enter driver name"
                    value={formData.driver_name} onChange={(e) => setFormData({...formData, driver_name: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Driver Phone</Form.Label>
                  <Form.Control type="tel" className="form-control-custom" placeholder="Enter phone number"
                    value={formData.driver_phone} onChange={(e) => setFormData({...formData, driver_phone: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label className="form-label-custom">Status</Form.Label>
              <Form.Select className="form-control-custom" value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="offline">Offline</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {editMode ? 'Save Changes' : 'Add Vehicle'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Vehicles;
